/**
 * HTML движок для интерактивной визуализации CSElement структур
 */

import {
  VisualizationEngine,
  VisualizationGraph,
  VisualizationOptions,
  VisualizationResult,
  VisualizationFormat,

  NodeStyle,
  EdgeStyle
} from '../../types/visualization-interfaces';

export class HtmlEngine implements VisualizationEngine {
  name = 'html';
  supportedFormats: VisualizationFormat[] = ['html'];

  /**
   * Проверка поддержки формата
   */
  supportsFormat(format: VisualizationFormat): boolean {
    return this.supportedFormats.includes(format);
  }

  /**
   * Рендеринг графа в HTML формат
   */
  async render(
    graph: VisualizationGraph,
    options: VisualizationOptions
  ): Promise<VisualizationResult> {
    const startTime = Date.now();
    
    const {
      width = 1000,
      height = 700,
      title = 'Визуализация CSElement',
      interactive = true,
      animations = true,
      showMinimap = false,
      nodeStyle = {},
      edgeStyle = {}
    } = options;

    // Генерируем HTML документ
    const htmlContent = this.generateHtml(graph, {
      width,
      height,
      title,
      interactive,
      animations,
      showMinimap,
      nodeStyle,
      edgeStyle
    });

    // Генерируем CSS
    const cssContent = this.generateCss(nodeStyle, edgeStyle, animations);

    // Генерируем JavaScript
    const jsContent = this.generateJavaScript(graph, {
      width,
      height,
      interactive,
      animations,
      showMinimap
    });

    return {
      content: htmlContent,
      format: 'html',
      metadata: {
        nodeCount: graph.nodes.length,
        edgeCount: graph.edges.length,
        maxDepth: graph.metadata.maxDepth,
        generationTime: Date.now() - startTime,
        dimensions: { width, height }
      },
      additionalFiles: {
        'visualization.css': cssContent,
        'visualization.js': jsContent
      }
    };
  }

  /**
   * Генерация HTML документа
   */
  private generateHtml(
    graph: VisualizationGraph,
    options: {
      width: number;
      height: number;
      title: string;
      interactive: boolean;
      animations: boolean;
      showMinimap: boolean;
      nodeStyle: NodeStyle;
      edgeStyle: EdgeStyle;
    }
  ): string {
    const { width, height, title, showMinimap } = options;

    return `<!DOCTYPE html>
<html lang="ru">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${this.escapeHtml(title)}</title>
    <link rel="stylesheet" href="visualization.css">
</head>
<body>
    <div class="visualization-container">
        <header class="visualization-header">
            <h1>${this.escapeHtml(title)}</h1>
            <div class="controls">
                <button id="zoom-in" title="Увеличить">🔍+</button>
                <button id="zoom-out" title="Уменьшить">🔍-</button>
                <button id="reset-zoom" title="Сбросить масштаб">🎯</button>
                <button id="toggle-layout" title="Сменить раскладку">🔄</button>
                <button id="export-svg" title="Экспорт SVG">💾</button>
                ${showMinimap ? '<button id="toggle-minimap" title="Миниатюра">🗺️</button>' : ''}
            </div>
        </header>
        
        <div class="visualization-content">
            <div class="main-canvas" id="main-canvas">
                <svg id="visualization-svg" width="${width}" height="${height}">
                    <defs>
                        <marker id="arrowhead" markerWidth="10" markerHeight="7" 
                                refX="9" refY="3.5" orient="auto">
                            <polygon points="0 0, 10 3.5, 0 7" fill="#666" />
                        </marker>
                        <filter id="glow">
                            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                            <feMerge> 
                                <feMergeNode in="coloredBlur"/>
                                <feMergeNode in="SourceGraphic"/>
                            </feMerge>
                        </filter>
                    </defs>
                    <g id="graph-container" transform="translate(0,0) scale(1)">
                        <!-- Граф будет добавлен через JavaScript -->
                    </g>
                </svg>
            </div>
            
            ${showMinimap ? this.generateMinimap() : ''}
        </div>
        
        <div class="info-panel" id="info-panel">
            <h3>Информация об элементе</h3>
            <div id="node-details">
                <p>Наведите курсор на элемент для просмотра деталей</p>
            </div>
        </div>
        
        <div class="stats-panel">
            <div class="stat">
                <span class="stat-label">Узлов:</span>
                <span class="stat-value">${graph.nodes.length}</span>
            </div>
            <div class="stat">
                <span class="stat-label">Связей:</span>
                <span class="stat-value">${graph.edges.length}</span>
            </div>
            <div class="stat">
                <span class="stat-label">Глубина:</span>
                <span class="stat-value">${graph.metadata.maxDepth}</span>
            </div>
        </div>
    </div>
    
    <script>
        // Встраиваем данные графа
        window.graphData = ${JSON.stringify(graph, null, 2)};
        window.visualizationOptions = ${JSON.stringify(options, null, 2)};
    </script>
    <script src="visualization.js"></script>
</body>
</html>`;
  }

  /**
   * Генерация миниатюры
   */
  private generateMinimap(): string {
    return `
            <div class="minimap-container" id="minimap-container">
                <h4>Навигация</h4>
                <svg id="minimap-svg" width="200" height="150">
                    <g id="minimap-graph">
                        <!-- Миниатюра будет добавлена через JavaScript -->
                    </g>
                    <rect id="viewport-indicator" fill="none" stroke="#007acc" stroke-width="2" />
                </svg>
            </div>`;
  }

  /**
   * Генерация CSS стилей
   */
  private generateCss(
    nodeStyle: NodeStyle,
    edgeStyle: EdgeStyle,
    animations: boolean
  ): string {
    const transition = animations ? 'transition: all 0.3s ease;' : '';
    
    return `
/* Основные стили */
* {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
}

body {
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    min-height: 100vh;
    color: #333;
}

.visualization-container {
    max-width: 100vw;
    height: 100vh;
    display: flex;
    flex-direction: column;
    background: white;
    box-shadow: 0 0 20px rgba(0,0,0,0.1);
}

/* Заголовок */
.visualization-header {
    background: #2c3e50;
    color: white;
    padding: 1rem 2rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.visualization-header h1 {
    font-size: 1.5rem;
    font-weight: 300;
}

.controls {
    display: flex;
    gap: 0.5rem;
}

.controls button {
    background: #3498db;
    border: none;
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.9rem;
    ${transition}
}

.controls button:hover {
    background: #2980b9;
    transform: translateY(-2px);
}

/* Основное содержимое */
.visualization-content {
    flex: 1;
    display: flex;
    position: relative;
    overflow: hidden;
}

.main-canvas {
    flex: 1;
    overflow: hidden;
    position: relative;
    background: #f8f9fa;
    background-image: 
        radial-gradient(circle at 1px 1px, rgba(0,0,0,.15) 1px, transparent 0);
    background-size: 20px 20px;
}

#visualization-svg {
    width: 100%;
    height: 100%;
    cursor: grab;
}

#visualization-svg:active {
    cursor: grabbing;
}

/* Узлы */
.node {
    cursor: pointer;
    ${transition}
    filter: drop-shadow(2px 2px 4px rgba(0,0,0,0.1));
}

.node:hover {
    filter: drop-shadow(4px 4px 8px rgba(0,0,0,0.2)) url(#glow);
    transform: scale(1.05);
}

.node.selected {
    filter: url(#glow);
}

.node-shape {
    fill: ${nodeStyle.backgroundColor || '#ffffff'};
    stroke: ${nodeStyle.borderColor || '#ddd'};
    stroke-width: ${nodeStyle.borderWidth || 2};
}

.node-text {
    fill: ${nodeStyle.textColor || '#333'};
    font-family: 'Segoe UI', sans-serif;
    font-size: ${nodeStyle.fontSize || 12}px;
    text-anchor: middle;
    dominant-baseline: middle;
    pointer-events: none;
}

/* Связи */
.edge {
    stroke: ${edgeStyle.color || '#666'};
    stroke-width: ${edgeStyle.width || 1.5};
    fill: none;
    marker-end: url(#arrowhead);
    ${transition}
}

.edge:hover {
    stroke-width: 3;
    stroke: #007acc;
}

/* Информационная панель */
.info-panel {
    width: 300px;
    background: white;
    border-left: 1px solid #ddd;
    padding: 1rem;
    overflow-y: auto;
    box-shadow: -2px 0 4px rgba(0,0,0,0.1);
}

.info-panel h3 {
    margin-bottom: 1rem;
    color: #2c3e50;
    border-bottom: 2px solid #3498db;
    padding-bottom: 0.5rem;
}

#node-details {
    font-size: 0.9rem;
    line-height: 1.6;
}

.detail-item {
    margin-bottom: 0.5rem;
    padding: 0.5rem;
    background: #f8f9fa;
    border-radius: 4px;
    border-left: 3px solid #3498db;
}

.detail-label {
    font-weight: bold;
    color: #2c3e50;
}

/* Статистика */
.stats-panel {
    position: absolute;
    top: 1rem;
    left: 1rem;
    background: rgba(255,255,255,0.9);
    padding: 1rem;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    backdrop-filter: blur(10px);
}

.stat {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.5rem;
    min-width: 120px;
}

.stat-label {
    font-weight: 500;
    color: #666;
}

.stat-value {
    font-weight: bold;
    color: #2c3e50;
}

/* Миниатюра */
.minimap-container {
    position: absolute;
    bottom: 1rem;
    right: 1rem;
    background: rgba(255,255,255,0.9);
    padding: 1rem;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    backdrop-filter: blur(10px);
}

.minimap-container h4 {
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
    color: #2c3e50;
}

#minimap-svg {
    border: 1px solid #ddd;
    border-radius: 4px;
}

/* Анимации */
${animations ? `
@keyframes nodeAppear {
    from {
        opacity: 0;
        transform: scale(0);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
}

@keyframes edgeAppear {
    from {
        stroke-dasharray: 1000;
        stroke-dashoffset: 1000;
    }
    to {
        stroke-dasharray: 1000;
        stroke-dashoffset: 0;
    }
}

.node {
    animation: nodeAppear 0.5s ease-out forwards;
}

.edge {
    animation: edgeAppear 1s ease-out forwards;
}
` : ''}

/* Адаптивность */
@media (max-width: 768px) {
    .visualization-content {
        flex-direction: column;
    }
    
    .info-panel {
        width: 100%;
        height: 200px;
        border-left: none;
        border-top: 1px solid #ddd;
    }
    
    .controls {
        flex-wrap: wrap;
    }
    
    .visualization-header {
        flex-direction: column;
        gap: 1rem;
    }
}`;
  }

  /**
   * Генерация JavaScript кода
   */
  private generateJavaScript(
    _graph: VisualizationGraph,
    _options: {
      width: number;
      height: number;
      interactive: boolean;
      animations: boolean;
      showMinimap: boolean;
    }
  ): string {
    return `
// Визуализация CSElement - JavaScript
class CSElementVisualization {
    constructor(graphData, options) {
        this.graphData = graphData;
        this.options = options;
        this.scale = 1;
        this.translateX = 0;
        this.translateY = 0;
        this.selectedNode = null;
        this.currentLayout = 'tree';
        
        this.init();
    }
    
    init() {
        this.setupEventListeners();
        this.renderGraph();
        if (this.options.showMinimap) {
            this.renderMinimap();
        }
    }
    
    setupEventListeners() {
        // Кнопки управления
        document.getElementById('zoom-in')?.addEventListener('click', () => this.zoom(1.2));
        document.getElementById('zoom-out')?.addEventListener('click', () => this.zoom(0.8));
        document.getElementById('reset-zoom')?.addEventListener('click', () => this.resetZoom());
        document.getElementById('toggle-layout')?.addEventListener('click', () => this.toggleLayout());
        document.getElementById('export-svg')?.addEventListener('click', () => this.exportSvg());
        
        // Перетаскивание
        const svg = document.getElementById('visualization-svg');
        let isDragging = false;
        let startX, startY;
        
        svg.addEventListener('mousedown', (e) => {
            if (e.target === svg || e.target.id === 'graph-container') {
                isDragging = true;
                startX = e.clientX - this.translateX;
                startY = e.clientY - this.translateY;
                svg.style.cursor = 'grabbing';
            }
        });
        
        svg.addEventListener('mousemove', (e) => {
            if (isDragging) {
                this.translateX = e.clientX - startX;
                this.translateY = e.clientY - startY;
                this.updateTransform();
            }
        });
        
        svg.addEventListener('mouseup', () => {
            isDragging = false;
            svg.style.cursor = 'grab';
        });
        
        // Масштабирование колесом мыши
        svg.addEventListener('wheel', (e) => {
            e.preventDefault();
            const delta = e.deltaY > 0 ? 0.9 : 1.1;
            this.zoom(delta);
        });
    }
    
    renderGraph() {
        const container = document.getElementById('graph-container');
        container.innerHTML = '';
        
        // Вычисляем позиции узлов
        const positions = this.calculatePositions();
        
        // Рендерим связи
        this.graphData.edges.forEach(edge => {
            const sourcePos = positions[edge.source];
            const targetPos = positions[edge.target];
            
            if (sourcePos && targetPos) {
                const edgeElement = this.createEdgeElement(sourcePos, targetPos, edge);
                container.appendChild(edgeElement);
            }
        });
        
        // Рендерим узлы
        this.graphData.nodes.forEach(node => {
            const position = positions[node.id];
            if (position) {
                const nodeElement = this.createNodeElement(node, position);
                container.appendChild(nodeElement);
            }
        });
    }
    
    calculatePositions() {
        const positions = {};
        const { width, height } = this.options;
        
        if (this.currentLayout === 'tree') {
            // Древовидная раскладка
            const levels = {};
            this.graphData.nodes.forEach(node => {
                if (!levels[node.level]) levels[node.level] = [];
                levels[node.level].push(node);
            });
            
            const maxLevel = Math.max(...Object.keys(levels).map(Number));
            
            Object.entries(levels).forEach(([level, nodes]) => {
                const y = (height / (maxLevel + 1)) * (parseInt(level) + 1);
                const nodeWidth = width / (nodes.length + 1);
                
                nodes.forEach((node, index) => {
                    positions[node.id] = {
                        x: nodeWidth * (index + 1),
                        y: y
                    };
                });
            });
        } else if (this.currentLayout === 'circular') {
            // Круговая раскладка
            const centerX = width / 2;
            const centerY = height / 2;
            const radius = Math.min(width, height) * 0.3;
            
            this.graphData.nodes.forEach((node, index) => {
                const angle = (2 * Math.PI * index) / this.graphData.nodes.length;
                positions[node.id] = {
                    x: centerX + radius * Math.cos(angle),
                    y: centerY + radius * Math.sin(angle)
                };
            });
        }
        
        return positions;
    }
    
    createNodeElement(node, position) {
        const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        group.classList.add('node');
        group.setAttribute('data-node-id', node.id);
        
        // Прямоугольник узла
        const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        rect.classList.add('node-shape');
        rect.setAttribute('x', position.x - 60);
        rect.setAttribute('y', position.y - 20);
        rect.setAttribute('width', 120);
        rect.setAttribute('height', 40);
        rect.setAttribute('rx', 8);
        
        // Текст узла
        const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        text.classList.add('node-text');
        text.setAttribute('x', position.x);
        text.setAttribute('y', position.y);
        text.textContent = this.truncateText(node.label, 15);
        
        group.appendChild(rect);
        group.appendChild(text);
        
        // События
        group.addEventListener('click', () => this.selectNode(node));
        group.addEventListener('mouseenter', () => this.showNodeDetails(node));
        group.addEventListener('mouseleave', () => this.hideNodeDetails());
        
        return group;
    }
    
    createEdgeElement(sourcePos, targetPos, edge) {
        const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        line.classList.add('edge');
        line.setAttribute('x1', sourcePos.x);
        line.setAttribute('y1', sourcePos.y);
        line.setAttribute('x2', targetPos.x);
        line.setAttribute('y2', targetPos.y);
        
        return line;
    }
    
    selectNode(node) {
        // Убираем выделение с предыдущего узла
        document.querySelectorAll('.node.selected').forEach(el => {
            el.classList.remove('selected');
        });
        
        // Выделяем новый узел
        const nodeElement = document.querySelector(\`[data-node-id="\${node.id}"]\`);
        if (nodeElement) {
            nodeElement.classList.add('selected');
        }
        
        this.selectedNode = node;
        this.showNodeDetails(node, true);
    }
    
    showNodeDetails(node, persistent = false) {
        const detailsContainer = document.getElementById('node-details');
        
        const details = \`
            <div class="detail-item">
                <div class="detail-label">Имя:</div>
                <div>\${node.label}</div>
            </div>
            <div class="detail-item">
                <div class="detail-label">ID:</div>
                <div>\${node.id}</div>
            </div>
            <div class="detail-item">
                <div class="detail-label">Уровень:</div>
                <div>\${node.level}</div>
            </div>
            \${node.data ? \`
                <div class="detail-item">
                    <div class="detail-label">Дочерних элементов:</div>
                    <div>\${node.data.childrenCount || 0}</div>
                </div>
                <div class="detail-item">
                    <div class="detail-label">Данных:</div>
                    <div>\${node.data.dataSize || 0}</div>
                </div>
            \` : ''}
        \`;
        
        detailsContainer.innerHTML = details;
    }
    
    hideNodeDetails() {
        if (!this.selectedNode) {
            const detailsContainer = document.getElementById('node-details');
            detailsContainer.innerHTML = '<p>Наведите курсор на элемент для просмотра деталей</p>';
        }
    }
    
    zoom(factor) {
        this.scale *= factor;
        this.scale = Math.max(0.1, Math.min(5, this.scale));
        this.updateTransform();
    }
    
    resetZoom() {
        this.scale = 1;
        this.translateX = 0;
        this.translateY = 0;
        this.updateTransform();
    }
    
    updateTransform() {
        const container = document.getElementById('graph-container');
        container.setAttribute('transform', 
            \`translate(\${this.translateX},\${this.translateY}) scale(\${this.scale})\`);
    }
    
    toggleLayout() {
        this.currentLayout = this.currentLayout === 'tree' ? 'circular' : 'tree';
        this.renderGraph();
    }
    
    exportSvg() {
        const svg = document.getElementById('visualization-svg');
        const serializer = new XMLSerializer();
        const svgString = serializer.serializeToString(svg);
        
        const blob = new Blob([svgString], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = 'cselement-visualization.svg';
        link.click();
        
        URL.revokeObjectURL(url);
    }
    
    truncateText(text, maxLength) {
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    }
}

// Инициализация при загрузке страницы
document.addEventListener('DOMContentLoaded', () => {
    if (window.graphData && window.visualizationOptions) {
        new CSElementVisualization(window.graphData, window.visualizationOptions);
    }
});`;
  }

  /**
   * Экранирование HTML
   */
  private escapeHtml(text: string): string {
    // Проверяем, доступен ли document (браузерная среда)
    if (typeof document !== 'undefined') {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }
    
    // Альтернативный метод для Node.js среды
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;');
  }
} 